import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useMyMfaState, useMfaEnroll, useMfaVerify, useListMfaFactors } from '@/data/hardening';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/lib/toast';

export const Route = createFileRoute('/admin/security')({
  component: AdminSecurity,
});

function AdminSecurity() {
  const state = useMyMfaState();
  const factors = useListMfaFactors();
  const enroll = useMfaEnroll();
  const verify = useMfaVerify();

  const [factorId, setFactorId] = useState<string | null>(null);
  const [qr, setQr] = useState<string | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState('');

  async function startEnroll() {
    // HIGH-4 (Sprint 10 review): re-clicking would create a second pending
    // TOTP factor in Supabase Auth that can never be verified but
    // still occupies a slot. Refuse to start a second enrollment while
    // one is already pending in this session.
    if (factorId || enroll.isPending) return;
    try {
      const r = await enroll.mutateAsync();
      setFactorId(r.factorId);
      setQr(r.qr);
      setSecret(r.secret);
    } catch (err) {
      toast({
        title: "Couldn't start enrollment",
        description: err instanceof Error ? err.message : 'Unknown error',
        variant: 'error',
      });
    }
  }

  async function confirm() {
    if (!factorId) return;
    try {
      await verify.mutateAsync({ factorId, code: code.trim() });
      toast({
        title: 'MFA enrolled',
        description: 'You are now at aal2. Recovery codes will be issued shortly.',
        variant: 'success',
      });
      setFactorId(null);
      setQr(null);
      setSecret(null);
      setCode('');
    } catch (err) {
      toast({
        title: 'Verification failed',
        description: err instanceof Error ? err.message : 'Wrong code',
        variant: 'error',
      });
    }
  }

  const totpFactors =
    factors.data?.totp ?? factors.data?.all?.filter((f) => f.factor_type === 'totp') ?? [];
  const enrolled = state.data?.enrolled_at != null && totpFactors.length > 0;

  return (
    <div className="space-y-10">
      <header>
        <p className="tracking-[var(--tracking-wide)] text-[var(--color-ink-muted)] text-[var(--text-sm)] uppercase">
          Operations
        </p>
        <h1 className="mt-2">Security</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Admin role requires two-factor authentication. Audit log access and destructive operations
          enforce this at the database row level — without aal2 you'll be denied silently.
        </p>
      </header>

      <section aria-labelledby="mfa-heading" className="surface-elevated space-y-4 p-6">
        <header className="flex items-baseline justify-between gap-3">
          <h2 id="mfa-heading" className="font-[var(--font-display)] text-[var(--text-xl)]">
            Two-factor authentication
          </h2>
          <Badge tone={enrolled ? 'success' : 'warn'}>
            {enrolled ? 'Enrolled' : 'Not enrolled'}
          </Badge>
        </header>

        {enrolled && (
          <p className="text-[var(--color-ink-muted)]">
            You enrolled on{' '}
            {state.data?.enrolled_at ? new Date(state.data.enrolled_at).toLocaleDateString() : '—'}.
            Your last aal2 sign-in was{' '}
            {state.data?.last_aal2_at
              ? new Date(state.data.last_aal2_at).toLocaleString()
              : 'never'}
            .
          </p>
        )}

        {!enrolled && !qr && (
          <>
            <p className="text-[var(--color-ink-muted)]">
              Set up an authenticator app (1Password, Authy, Google Authenticator). You'll scan a QR
              code and enter the 6-digit code to confirm.
            </p>
            <Button onClick={() => void startEnroll()} disabled={enroll.isPending}>
              {enroll.isPending ? 'Generating…' : 'Begin enrollment'}
            </Button>
          </>
        )}

        {qr && factorId && (
          <div className="grid gap-4 sm:grid-cols-[auto_1fr] sm:items-center">
            <img
              src={qr}
              alt="Authenticator QR code"
              className="size-40 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-white p-2"
            />
            <div>
              <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                Or enter this secret manually:
              </p>
              <code className="block rounded-[var(--radius-sm)] bg-[var(--color-surface-sunken)] p-2 font-mono break-all text-[var(--text-sm)]">
                {secret}
              </code>
              <p className="mt-1 text-[var(--color-ink-subtle)] text-[var(--text-xs)]">
                Uses letters A–Z and digits 2–7 only — there is no 0 (zero), 1, 8, or 9. The letter
                “O” is a letter, not a zero.
              </p>
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="mfa-code">6-digit code</Label>
              <Input
                id="mfa-code"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="123 456"
                inputMode="numeric"
                className="font-mono tracking-widest"
              />
              <Button
                className="mt-3"
                onClick={() => void confirm()}
                disabled={code.length !== 6 || verify.isPending}
              >
                {verify.isPending ? 'Verifying…' : 'Confirm'}
              </Button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
